import React from 'react';

/**
 * Web stub for @react-native-community/slider
 * Provides a simple range input implementation for web platform
 */
const Slider = React.forwardRef((props, ref) => {
  const {
    style,
    minimumValue = 0,
    maximumValue = 100,
    value = 0,
    step = 1,
    onValueChange,
    onSlidingComplete,
    minimumTrackTintColor = '#007AFF',
    maximumTrackTintColor = '#E0E0E0',
    thumbColor = '#FFFFFF',
    disabled = false,
  } = props;

  const handleChange = (e) => {
    const newValue = parseFloat(e.target.value);
    if (onValueChange) {
      onValueChange(newValue);
    }
  };

  const handleTouchEnd = (e) => {
    const newValue = parseFloat(e.target.value);
    if (onSlidingComplete) {
      onSlidingComplete(newValue);
    }
  };

  // Calculate the percentage of the slider that's filled
  const percentage = ((value - minimumValue) / (maximumValue - minimumValue)) * 100;

  const sliderStyle = {
    ...style,
    width: style?.width || '100%',
    height: style?.height || 30,
    WebkitAppearance: 'none',
    appearance: 'none',
    borderRadius: '3px',
    outline: 'none',
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.5 : 1,
    // Dynamic background using CSS gradient
    background: `linear-gradient(to right, ${minimumTrackTintColor} 0%, ${minimumTrackTintColor} ${percentage}%, ${maximumTrackTintColor} ${percentage}%, ${maximumTrackTintColor} 100%)`,
  };

  return (
    <>
      <input
        ref={ref}
        type="range"
        min={minimumValue}
        max={maximumValue}
        step={step}
        value={value}
        onChange={handleChange}
        onTouchEnd={handleTouchEnd}
        onMouseUp={handleTouchEnd}
        disabled={disabled}
        style={sliderStyle}
      />
      <SliderThumbStyles thumbColor={thumbColor} minimumTrackTintColor={minimumTrackTintColor} />
    </>
  );
});

Slider.displayName = 'Slider';

/**
 * Component to dynamically inject CSS for thumb styling
 */
function SliderThumbStyles({ thumbColor, minimumTrackTintColor }) {
  const styleId = `slider-thumb-style-${thumbColor.replace(/[^a-zA-Z0-9]/g, '')}`;
  
  React.useEffect(() => {
    const existingStyle = document.getElementById(styleId);
    if (!existingStyle) {
      const style = document.createElement('style');
      style.id = styleId;
      style.innerHTML = `
        input[type='range']::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: ${thumbColor};
          cursor: pointer;
          border: 2px solid ${minimumTrackTintColor};
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        input[type='range']::-moz-range-thumb {
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: ${thumbColor};
          cursor: pointer;
          border: 2px solid ${minimumTrackTintColor};
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        input[type='range']:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
      `;
      document.head.appendChild(style);
    }

    return () => {
      // Cleanup if needed
    };
  }, [thumbColor, minimumTrackTintColor, styleId]);

  return null;
}

// Initialize base styles once
if (typeof document !== 'undefined') {
  const styleId = 'slider-web-stub-base-styles';
  if (!document.getElementById(styleId)) {
    const style = document.createElement('style');
    style.id = styleId;
    style.innerHTML = `
      input[type='range'] {
        -webkit-appearance: none;
        appearance: none;
        width: 100%;
        padding: 0;
        margin: 0;
      }

      input[type='range']::-webkit-slider-runnable-track {
        background: transparent;
        border: none;
        border-radius: 3px;
      }

      input[type='range']::-moz-range-track {
        background: transparent;
        border: none;
        border-radius: 3px;
      }

      input[type='range']::-webkit-slider-thumb {
        -webkit-appearance: none;
        appearance: none;
      }

      input[type='range']:focus {
        outline: none;
      }
    `;
    document.head.appendChild(style);
  }
}

export default Slider;
